import { useContext } from 'react';
import {
  Box, Card, Stack, Typography, Button, Input, CircularProgress,
  Tabs, TabList, Tab, tabClasses, useTheme,
} from '@mui/joy';
import useMediaQuery from '@mui/material/useMediaQuery';
import { motion } from 'framer-motion';
import Cookies from 'js-cookie';
import { LayoutContext } from '../../../context';

// ── Форматтеры ──
export const fmtMoney = (v) =>
  (v === null || v === undefined || v === '')
    ? '—'
    : Number(v).toLocaleString('ru-RU', { maximumFractionDigits: 0 }) + ' ₽';
export const fmtNum = (v) => (v === null || v === undefined) ? '—' : Number(v).toLocaleString('ru-RU');
export const fmtPct = (v) =>
  (v === null || v === undefined) ? '—' : Number(v).toLocaleString('ru-RU', { maximumFractionDigits: 1 }) + ' %';

// ── Даты ──
export const todayISO = () => new Date().toISOString().slice(0, 10);
export const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10); };
export const monthStartISO = () => { const d = new Date(); d.setDate(1); return d.toISOString().slice(0, 10); };

// ── Токен ──
export function useFinanceToken() {
  const { tokenEmployee } = useContext(LayoutContext);
  return Cookies.get('auth_token') || tokenEmployee;
}

// ── Мобильный брейкпоинт (< sm) ──
export function useIsMobile() {
  const theme = useTheme();
  return useMediaQuery(theme.breakpoints.down('sm'));
}

// ── Цвета светофора ──
export const LIGHT_COLORS = { green: '#16a34a', yellow: '#eab308', red: '#ef4444', gray: '#94a3b8' };

// ── Пресет анимации появления ──
export const fadeUp = { initial: { opacity: 0, y: 10 }, animate: { opacity: 1, y: 0 } };

// ── Шапка раздела: градиентная иконка + заголовок + правый слот действий ──
export function PageHeader({ icon, title, subtitle, actions }) {
  return (
    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: { xs: 2, md: 3 }, px: { xs: 2, md: 0 } }}>
      <Box sx={{
        width: 42, height: 42, flexShrink: 0, borderRadius: '13px',
        background: 'linear-gradient(135deg,#6366f1,#8b5cf6)',
        boxShadow: '0 4px 14px rgba(99,102,241,0.3)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff',
      }}>
        {icon}
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography sx={{
          fontSize: { xs: '1.4rem', md: '1.7rem' }, fontWeight: 800,
          fontFamily: "'Sora', sans-serif", letterSpacing: '-0.5px', lineHeight: 1.1,
        }}>
          {title}
        </Typography>
        {subtitle && <Typography level="body-xs" sx={{ color: 'text.secondary', mt: 0.25 }}>{subtitle}</Typography>}
      </Box>
      {actions}
    </Stack>
  );
}

// ── Вкладки-пилюли (горизонтальный скролл на мобилке, без полосы прокрутки) ──
export function FinanceTabs({ tabs, value, onChange }) {
  return (
    <Tabs value={value} onChange={(_, v) => v && onChange(v)} sx={{ bgcolor: 'transparent' }}>
      <TabList
        disableUnderline
        sx={{
          gap: 0.5, p: 0.5, borderRadius: 'xl', bgcolor: 'background.level1',
          flexWrap: 'nowrap', overflowX: 'auto',
          scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' },
          [`& .${tabClasses.root}`]: {
            flex: 'none', whiteSpace: 'nowrap', borderRadius: 'lg',
            fontWeight: 600, fontSize: '0.85rem', px: 1.75, py: 0.75, transition: 'all .15s',
            [`&:not(.${tabClasses.selected})`]: { color: 'text.secondary' },
            [`&.${tabClasses.selected}`]: {
              bgcolor: 'background.surface', color: 'primary.plainColor', boxShadow: 'sm',
            },
          },
        }}
      >
        {tabs.map((t) => <Tab key={t.key} value={t.key} disableIndicator>{t.label}</Tab>)}
      </TabList>
    </Tabs>
  );
}

// ── Карточка-секция для графиков и таблиц ──
export function SectionCard({ title, action, children, sx }) {
  return (
    <Card variant="outlined" sx={{ p: { xs: 1.5, md: 2 }, borderRadius: 'xl', mb: 2, ...sx }}>
      {(title || action) && (
        <Stack direction="row" justifyContent="space-between" alignItems="center"
          sx={{ mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
          {title && <Typography level="title-sm" sx={{ fontWeight: 700 }}>{title}</Typography>}
          {action}
        </Stack>
      )}
      {children}
    </Card>
  );
}

// ── Адаптивная сетка метрик (2 колонки на мобилке, авто на десктопе) ──
export function MetricGrid({ children }) {
  return (
    <Box sx={{
      display: 'grid', gap: 1.25, mb: 2,
      gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(auto-fit, minmax(180px, 1fr))' },
    }}>
      {children}
    </Box>
  );
}

// ── Быстрые пресеты периода ──
const PRESETS = [
  { label: '7 дней',  from: () => daysAgo(6) },
  { label: '30 дней', from: () => daysAgo(29) },
  { label: '90 дней', from: () => daysAgo(89) },
  { label: 'Месяц',   from: () => monthStartISO() },
];

export function PeriodPresets({ onPick }) {
  return (
    <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
      {PRESETS.map((p) => (
        <Button key={p.label} size="sm" variant="soft" color="neutral"
          onClick={() => onPick(p.from(), todayISO())} sx={{ borderRadius: 'lg' }}>
          {p.label}
        </Button>
      ))}
    </Stack>
  );
}

// ── Панель периода: пресеты + даты + индикатор загрузки + правый слот ──
// На мобилке колонки складываются вертикально.
export function PeriodBar({ from, to, onFrom, onTo, onPreset, loading, right }) {
  return (
    <Card variant="soft" color="neutral"
      sx={{ p: 1, mb: 2, borderRadius: 'xl', bgcolor: 'background.level1', boxShadow: 'none' }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}
        alignItems={{ xs: 'stretch', md: 'center' }} sx={{ gap: 1 }}>
        {onPreset && <PeriodPresets onPick={onPreset} />}
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', gap: 1 }}>
          <Input type="date" size="sm" value={from} onChange={(e) => onFrom(e.target.value)} sx={{ borderRadius: 'lg' }} />
          <Typography level="body-sm">—</Typography>
          <Input type="date" size="sm" value={to} onChange={(e) => onTo(e.target.value)} sx={{ borderRadius: 'lg' }} />
          {loading && <CircularProgress size="sm" />}
        </Stack>
        {right && (
          <>
            <Box sx={{ flex: 1, display: { xs: 'none', md: 'block' } }} />
            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>{right}</Stack>
          </>
        )}
      </Stack>
    </Card>
  );
}

// ── Стиль для таблиц: горизонтальный скролл + «липкая» первая колонка на мобилке ──
// Применяется к <Table sx={scrollTableSx(minWidth)} /> внутри <Sheet sx={{ overflow: 'auto' }} />.
export const scrollTableSx = (minWidth = 720) => ({
  minWidth,
  '& thead th:first-child, & tbody td:first-child': {
    position: 'sticky', left: 0, zIndex: 2, bgcolor: 'background.surface',
  },
  '& thead th:first-child': { zIndex: 4 },
});

// ── Карточка метрики: цветная подложка + светофор + hover-подъём + анимация появления ──
// Совместима со старым вызовом (title, value, sub, color, light); добавлен опциональный index для stagger.
export function MetricCard({ title, value, sub, color = '#6366f1', light, index = 0 }) {
  return (
    <Box
      component={motion.div}
      initial={fadeUp.initial}
      animate={fadeUp.animate}
      transition={{ duration: 0.25, delay: Math.min(index, 8) * 0.03 }}
      sx={{ flex: '1 1 180px', minWidth: 150 }}
    >
      <Card sx={{
        p: 1.5, borderRadius: '18px', gap: 0,
        border: '1.5px solid', borderColor: `${color}22`,
        height: "-webkit-fill-available",
        background: `linear-gradient(135deg, ${color}0d 0%, ${color}04 100%)`,
        boxShadow: 'none', transition: 'transform .15s, box-shadow .15s',
        '&:hover': { transform: 'translateY(-2px)', boxShadow: 'md' },
      }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 0.25 }}>
          <Typography level="body-xs" sx={{ color: 'text.secondary', fontWeight: 600 }}>{title}</Typography>
          {light && (
            <Box sx={{
              width: 10, height: 10, borderRadius: '50%', mt: 0.3, flexShrink: 0,
              bgcolor: LIGHT_COLORS[light] || LIGHT_COLORS.gray,
              boxShadow: `0 0 0 3px ${(LIGHT_COLORS[light] || LIGHT_COLORS.gray)}22`,
            }} />
          )}
        </Stack>
        <Typography sx={{ fontSize: { xs: '1.35rem', md: '1.7rem' }, fontWeight: 800, color, lineHeight: 1.15 }}>
          {value}
        </Typography>
        {sub && <Typography level="body-xs" sx={{ color: 'text.tertiary', mt: 0.3 }}>{sub}</Typography>}
      </Card>
    </Box>
  );
}